import { twMerge } from 'tailwind-merge';

import { usePostViewer } from '../../helpers/hooks/usePostViewer';
import {
  useIsMobileScreenWidthOrThinner,
  useIsTabletScreenWidthOrThinner,
} from '../../helpers/hooks/useScreenWidth';
import { Heading } from '../Heading/Heading';
import { MAIN_NAVIGATION_MIN_HEIGHT } from '../Navigation/MainNavigation/constants/constants';
import { PostsViewer } from '../PostsViewer/PostsViewer';
import { openPostViewerEventSources } from '../PostsViewer/types/openPostViewerEventSources';

import { ReactNode } from 'react';
import { FishingReportCTASection } from './components/FishingReportCTASection';
import { FishingReportCard, PostExtended } from './components/FishingReportCard';
import {
  FISHINGREPORT_SECTION_MAX_CATCHES_DESKTOP,
  FISHINGREPORT_SECTION_MAX_CATCHES_MOBILE,
  FISHINGREPORT_SECTION_MAX_CATCHES_TABLET,
} from './constants/constants';

interface IProps {
  className?: string;
  id: string;
  recentCatchPostList: PostExtended[];
  postsReference: React.RefObject<HTMLDivElement>;
  postViewerShowMoreTeaser?: ReactNode;
  showFishingWaterName?: boolean;
  title: string;
}

export const FishingReportsGrid = ({
  className,
  id = 'fishing-reports-grid',
  recentCatchPostList,
  postsReference,
  postViewerShowMoreTeaser,
  showFishingWaterName = false,
  title = 'Fishing reports',
}: IProps) => {
  const {
    currentPostViewerId,
    closePostViewer,
    isPostViewerOpen,
    openPostViewer,
    setCurrentPostViewerId,
  } = usePostViewer(postsReference, MAIN_NAVIGATION_MIN_HEIGHT + 4);

  const isTabletScreenWidthOrThinner = useIsTabletScreenWidthOrThinner();
  const isMobileScreenWidthOrThinner = useIsMobileScreenWidthOrThinner();

  const postViewerMaxCatchCount = isMobileScreenWidthOrThinner
    ? FISHINGREPORT_SECTION_MAX_CATCHES_MOBILE
    : isTabletScreenWidthOrThinner
      ? FISHINGREPORT_SECTION_MAX_CATCHES_TABLET
      : FISHINGREPORT_SECTION_MAX_CATCHES_DESKTOP;

  return (
    <div
      id={id}
      className={twMerge(
        'w-full scroll-mt-[--navigation-scrolltop-offset-mobile] sm:scroll-mt-[--navigation-scrolltop-offset]',
        className,
      )}
    >
      <Heading className="mb-6 sm:mb-10" variant="sm" as="h2">
        {title}
      </Heading>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-12 sm:gap-4">
        {recentCatchPostList.map((post, index) => (
          <FishingReportCard
            key={post._id}
            post={post}
            className={`col-span-2 sm:col-span-4 md:col-span-3 ${index >= FISHINGREPORT_SECTION_MAX_CATCHES_MOBILE ? 'hidden' : ''} ${index >= FISHINGREPORT_SECTION_MAX_CATCHES_TABLET ? 'sm:hidden' : ''} ${index >= FISHINGREPORT_SECTION_MAX_CATCHES_DESKTOP ? 'md:hidden' : ''}`}
            openPostViewer={openPostViewer}
            openPostViewerEventSource={openPostViewerEventSources.FishingReportRecentCatch}
            showFishingWaterName={showFishingWaterName}
          />
        ))}
      </div>
      <FishingReportCTASection catchCount={recentCatchPostList.length} />
      {isPostViewerOpen && currentPostViewerId && (
        <PostsViewer
          posts={recentCatchPostList.slice(0, postViewerMaxCatchCount)}
          currentPostViewerId={currentPostViewerId}
          hasMore={false}
          isPostViewerOpen
          loadMore={(): void => {}}
          onClosePostsViewer={closePostViewer}
          setCurrentPostViewerId={setCurrentPostViewerId}
          showMoreTeaser={postViewerShowMoreTeaser}
        />
      )}
    </div>
  );
};
